import { cloneDeep } from 'lodash'

import { isEmpty, sendingsDefaultViberButton, sendingsDefaultWhatsAppButton, sendingsPayloadFieldsByType } from '@sigma-frontend-libs/commons'
import { ETTSTypes } from '@sigmasms/shared-types'

/**
 * Фабрики дефолтных значений для полей payload, у которых есть начальная форма.
 * Ключ - имя поля payload, значение - функция, возвращающая свежий дефолт
 * (чтобы исключить общий изменяемый объект между payload-ами).
 * Поля image/file/audio дефолта не имеют: появляются в payload только после прикрепления.
 * @type {Object<string, () => unknown>}
 */
const payloadFieldDefaults = {
  tts: () => {
    return ETTSTypes['yandex:filipp']
  },
  button: () => {
    return cloneDeep(sendingsDefaultViberButton)
  },
  buttons: () => {
    return [ cloneDeep(sendingsDefaultWhatsAppButton) ]
  }
}

/**
 * Строит payload по умолчанию для указанного типа рассылки.
 * Базовые поля text и sender присутствуют всегда. Допустимые для типа поля
 * берутся из таблицы sendingsPayloadFieldsByType (commons): для полей с дефолтом
 * (tts, button, buttons) подставляется значение из payloadFieldDefaults,
 * остальные (image/file/audio) в дефолтный payload не добавляются.
 * @param {import('@sigmasms/shared-types').SendingTypes} type - тип рассылки
 * @returns {Object} payload по умолчанию (text, sender и поля типа с дефолтом)
 */
export function getDefaultPayload(type) {
  const result = {
    text: '',
    sender: ''
  }
  const fields = sendingsPayloadFieldsByType[type] ?? []
  for (const field of fields) {
    if (payloadFieldDefaults[field]) {
      result[field] = payloadFieldDefaults[field]()
    }
  }

  return result
}

/**
 * Нормализует массив кнопок WhatsApp/VK.
 * Пустой ввод или не-массив заменяется массивом с одной дефолтной кнопкой.
 * Для каждой кнопки очищенные (пустые) поля text/action приводятся к пустой
 * строке (канон commons), непустые - сохраняются.
 * @param {Array<{ text?: string, action?: string }>} buttons - исходные кнопки
 * @returns {Array<{ text: string, action: string }>} нормализованные кнопки
 */
function normalizeButtons(buttons) {
  if (!Array.isArray(buttons) || buttons.length === 0) {
    return [ cloneDeep(sendingsDefaultWhatsAppButton) ]
  }

  return buttons.map(button => {
    if (!button) {
      return cloneDeep(sendingsDefaultWhatsAppButton)
    }

    let text = ''
    let action = ''
    if (!isEmpty(button.text)) {
      text = button.text
    }
    if (!isEmpty(button.action)) {
      action = button.action
    }

    return {
      text,
      action
    }
  })
}

/**
 * Нормализует кнопку Viber.
 * Отсутствующая кнопка заменяется дефолтной. Очищенные (пустые) поля text/url
 * приводятся к пустой строке (канон commons), непустые - сохраняются.
 * @param {{ text?: string, url?: string }} button - исходная кнопка
 * @returns {{ text: string, url: string }} нормализованная кнопка
 */
function normalizeButton(button) {
  if (!button) {
    return cloneDeep(sendingsDefaultViberButton)
  }

  let text = ''
  let url = ''
  if (!isEmpty(button.text)) {
    text = button.text
  }
  if (!isEmpty(button.url)) {
    url = button.url
  }

  return {
    text,
    url
  }
}

/**
 * Нормализует payload под указанный тип рассылки.
 * Берёт payload по умолчанию и наполняет его допустимыми для типа полями
 * из таблицы sendingsPayloadFieldsByType: button/buttons прогоняются через
 * normalizeButton/normalizeButtons, остальные attachment-поля (image/file/audio)
 * копируются как есть; пустые значения пропускаются. Базовые поля (text, sender)
 * и дефолтные (например tts), не входящие в таблицу, копируются из value, если не пустые.
 * @param {import('@sigmasms/shared-types').SendingTypes} type - тип рассылки
 * @param {Object} [value={}] - исходные значения payload
 * @returns {Object} нормализованный payload
 */
export function normalizePayload(type, value = {}) {
  const result = cloneDeep(getDefaultPayload(type))
  const fields = sendingsPayloadFieldsByType[type] ?? []

  for (const field of fields) {
    if (isEmpty(value[field])) {
      continue
    }
    if (field === 'button') {
      result.button = normalizeButton(value.button)
    } else if (field === 'buttons') {
      result.buttons = normalizeButtons(value.buttons)
    } else {
      result[field] = cloneDeep(value[field])
    }
  }

  for (const key of Object.keys(result)) {
    if (fields.includes(key)) {
      continue
    }

    if (!isEmpty(value[key])) {
      result[key] = cloneDeep(value[key])
    }
  }

  return result
}
